// Dashboard. Talks to WaveHouse only through @wavehouse/sdk — no raw fetch, no
// hand-rolled EventSource — so every page load exercises the SDK in a browser.
import { createClient } from "@wavehouse/sdk";
import { initAnalytics } from "./analytics.js";

// Baked in at build time from wrangler.jsonc `vars` (see build.mjs) — nothing about
// a particular deployment is committed in this file.
/* global __WH_BASE_URL__, __GH_REPO__, __STALE_AFTER_S__ */
const WH_BASE_URL = __WH_BASE_URL__;
const GH_REPO = __GH_REPO__;

// No auth: the policy's `default_role: public` covers reads and SSE alike.
// Cross-origin, allowed by the project's `cors_allowed_origins`.
const wh = createClient({ baseURL: WH_BASE_URL });

const $ = (id) => document.getElementById(id);
const fmtInt = (n) => Number(n).toLocaleString("en-US");
const esc = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Normalize a WaveHouse timestamp to a UTC epoch (ms). WaveHouse serializes event_ts
// TWO ways: the /v1/query backfill returns ISO-8601 with a trailing `Z` (UTC), but the
// /v1/stream SSE frames return ClickHouse-native "YYYY-MM-DD HH:MM:SS.fff" with NO zone
// — which Date.parse reads as LOCAL time, so live rows would land on a different clock
// than backfilled ones (hours off, and the skew flips sign by viewer timezone). Forcing
// UTC (space→T, append Z if absent) keeps ordering AND relative times consistent across
// the backfill↔stream seam. (Upstream SSE/query format mismatch — WaveHouse #372.)
const parseTs = (s) => {
  const x = String(s ?? "").replace(" ", "T");
  return Date.parse(x + (x.endsWith("Z") ? "" : "Z"));
};

function relTime(iso) {
  const t = parseTs(iso);
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 45) return "just now";
  if (s < 90) return "1m ago";
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 5400) return "1h ago";
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

// --- Counts (named pipes) ---------------------------------------------------
async function loadCount(elId, pipeName) {
  const el = $(elId);
  const { ok, data, error } = await wh.pipe(pipeName).fetch();
  if (!ok) {
    el.textContent = "—";
    el.title = `${pipeName}: ${error?.status ?? ""} ${error?.message ?? "unavailable"}`.trim();
    return;
  }
  const row = data?.[0] ?? {};
  const v = row.total ?? row.count ?? Object.values(row)[0] ?? 0;
  el.textContent = fmtInt(v);
  el.title = "";
}

let countTimer = null;
function refreshCounts() {
  clearTimeout(countTimer);
  countTimer = setTimeout(() => {
    loadCount("stat-stars", "gh_stars_total");
    loadCount("stat-forks", "gh_forks_total");
  }, 400); // debounce bursts of streamed events
}

// --- Sparkline (gh_stars_daily pipe) ---------------------------------------
async function loadChart() {
  const { ok, data } = await wh.pipe("gh_stars_daily").fetch();
  if (!ok || !data?.length) {
    $("chart-empty").hidden = false;
    if (!ok) $("chart-empty").textContent = "Chart unavailable.";
    return;
  }
  const vals = data.map((r) => Number(r.stars_added) || 0);
  const max = Math.max(1, ...vals);
  const W = 600, H = 90, pad = 4, n = vals.length;
  const x = (i) => (n === 1 ? W / 2 : pad + (i * (W - 2 * pad)) / (n - 1));
  const y = (v) => H - pad - (v / max) * (H - 2 * pad);
  const line = vals.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  $("spark-line").setAttribute("d", line);
  $("spark-area").setAttribute("d", `${line} L${x(n - 1).toFixed(1)} ${H} L${x(0).toFixed(1)} ${H} Z`);
  const total = vals.reduce((a, b) => a + b, 0);
  $("chart-meta").textContent = `${fmtInt(total)} over ${n} day${n === 1 ? "" : "s"}`;
}

// --- Real-time latency proof (#33) -------------------------------------------
// ev.timestamp is WaveHouse's received_timestamp — the server's ingest clock,
// delivered on EVERY SSE event. now − that = the WaveHouse-owned real-time path
// (ingest → WAL → SSE broadcast → your browser): the thing pointing a frontend
// at ClickHouse can't do. Browser↔server clock skew makes a single sample noisy,
// so we clamp out-of-range samples and show the latest sane value, recent median
// in the tooltip. Honest framing: "ingested → your browser", network included.
const latRing = [];
function recordLatency(isoTs) {
  const ms = Date.now() - Date.parse(isoTs);
  if (!Number.isFinite(ms) || ms < 0 || ms > 60_000) return; // clock skew / stale — drop
  latRing.push(ms);
  if (latRing.length > 30) latRing.shift();
  const med = [...latRing].sort((a, b) => a - b)[Math.floor(latRing.length / 2)];
  const banner = $("latency"), msEl = $("lat-ms");
  if (msEl) msEl.textContent = fmtInt(Math.round(ms));
  if (banner) {
    banner.hidden = false;
    banner.title = `ingest → your browser · latest ${Math.round(ms)} ms · recent median ${Math.round(med)} ms (n=${latRing.length})`;
  }
}

// --- Live throughput sparkline (#34) -----------------------------------------
// Backfilled once from gh_events_per_minute, then the latest minute bucket is
// bumped live as the SSE stream ticks — so a viewer sees the stream is alive.
const minuteMs = parseTs; // same UTC-normalizing parse (see parseTs)
let epm = []; // [{ t: epoch-ms of minute, n: count }]
async function loadEpm() {
  const { ok, data } = await wh.pipe("gh_events_per_minute").fetch();
  if (ok && Array.isArray(data)) {
    epm = data.map((r) => ({ t: minuteMs(r.minute), n: Number(r.events) || 0 })).filter((r) => Number.isFinite(r.t));
  }
  drawEpm();
}
function bumpEpm() {
  const m = Math.floor(Date.now() / 60_000) * 60_000; // current minute (UTC epoch)
  const last = epm[epm.length - 1];
  if (last && last.t === m) last.n++;
  else epm.push({ t: m, n: 1 });
  while (epm.length > 60) epm.shift();
  drawEpm();
}
function drawEpm() {
  // Densify to a FIXED 60-minute axis (oldest→newest), zero-filled. The sparse
  // pipe result only carries minutes that had events, so plotting it by array
  // index (a) misplaced spikes on the timeline and (b) drew NOTHING when a single
  // minute had all the events (a lone `M` moveto). A real 60-bucket axis always
  // renders and puts each spike at its true position.
  const N = 60, nowM = Math.floor(Date.now() / 60_000) * 60_000;
  const byT = new Map(epm.map((r) => [r.t, r.n]));
  const vals = [];
  for (let i = N - 1; i >= 0; i--) vals.push(byT.get(nowM - i * 60_000) || 0);
  const total = vals.reduce((a, b) => a + b, 0);
  const lineEl = $("epm-line"), areaEl = $("epm-area");
  if (total === 0) {
    lineEl.removeAttribute("d"); areaEl.removeAttribute("d");
    $("epm-empty").hidden = false; $("epm-rate").textContent = "";
    return;
  }
  $("epm-empty").hidden = true;
  const max = Math.max(1, ...vals), W = 600, H = 90, pad = 4, n = vals.length;
  const x = (i) => pad + (i * (W - 2 * pad)) / (n - 1);
  const y = (v) => H - pad - (v / max) * (H - 2 * pad);
  const line = vals.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  lineEl.setAttribute("d", line);
  areaEl.setAttribute("d", `${line} L${x(n - 1).toFixed(1)} ${H} L${x(0).toFixed(1)} ${H} Z`);
  $("epm-rate").textContent = `${fmtInt(total)} in the last hour`;
}
// Lifetime "N events tracked" headline (gh_summary.total_events).
async function loadSummary() {
  const { ok, data } = await wh.pipe("gh_summary").fetch();
  const total = ok ? data?.[0]?.total_events : null;
  if (total != null) $("epm-total").textContent = `${fmtInt(total)} tracked · live`;
}

// --- Activity feed (live query: backfill + stream, all via the SDK) ---------
const VERBS = {
  "star:created": ["starred the repo", "accent"],
  "star:deleted": ["unstarred the repo", "muted"],
  "fork:created": ["forked the repo", "accent"],
  "issues:opened": ["opened an issue", "emerald"],
  "issues:closed": ["closed an issue", "rose"],
  "issues:reopened": ["reopened an issue", "amber"],
  "pull_request:opened": ["opened a pull request", "emerald"],
  "pull_request:closed": ["closed a pull request", "rose"],
  "pull_request:reopened": ["reopened a pull request", "amber"],
  "pull_request_review:submitted": ["reviewed a pull request", "emerald"],
  "release:published": ["published a release", "amber"],
  "member:added": ["was added as a member", "accent"],
  "push": ["pushed", "accent"],
  "create:created": ["created", "emerald"],
  "delete:deleted": ["deleted", "rose"],
  "gollum": ["edited the wiki", "muted"],
  "discussion:created": ["started a discussion", "emerald"],
  "deployment:created": ["started a deploy", "amber"],
  "deployment_status:success": ["deployed", "emerald"],
  "deployment_status:failure": ["deploy failed", "rose"],
  "label:created": ["added a label", "muted"],
  "milestone:created": ["created a milestone", "muted"],
};
// CI / check events arrive many-per-push and carry their status in `title` (the
// payload is hidden from the public role), so derive the tone from the conclusion
// word in the title rather than a fixed per-type color.
const CI_TYPES = new Set(["workflow_run", "workflow_job", "check_run", "check_suite", "status"]);
function ciTone(title) {
  const t = (title || "").toLowerCase();
  if (/success|passed/.test(t)) return "emerald";
  if (/fail|error|cancel|timed_out|action_required/.test(t)) return "rose";
  return "amber"; // queued / in_progress / pending / neutral
}
// Comment events carry a lifecycle (created / edited / deleted) rather than a single
// verb — GitHub fires all three (commit_comment is created-only). Render the whole
// lifecycle so an edit or a delete is visible in the feed, not just the first post,
// and tag which surface it was on (PR review, commit, discussion, else the issue).
const COMMENT_TYPES = new Set(["issue_comment", "pull_request_review_comment", "commit_comment", "discussion_comment"]);
function commentVerb(row) {
  const where = row.event_type === "pull_request_review_comment" ? " on a review"
    : row.event_type === "commit_comment" ? " on a commit"
    : row.event_type === "discussion_comment" ? " on a discussion" : "";
  if (row.action === "edited") return [`edited a comment${where}`, "amber"];
  if (row.action === "deleted") return [`deleted a comment${where}`, "rose"];
  return [`commented${where}`, "muted"]; // created (and any future action)
}
function describe(row) {
  if (CI_TYPES.has(row.event_type)) {
    const verb = row.event_type === "status" ? "commit status"
      : row.event_type === "workflow_run" ? "CI run"
      : row.event_type === "workflow_job" ? "CI job" : "check";
    return [verb, ciTone(row.title)];
  }
  if (COMMENT_TYPES.has(row.event_type)) return commentVerb(row);
  const key = row.action ? `${row.event_type}:${row.action}` : row.event_type;
  return VERBS[key] || VERBS[row.event_type] || [`${row.event_type}${row.action ? " · " + row.action : ""}`, "muted"];
}
function detailText(row) {
  const num = Number(row.number) > 0 ? `#${row.number}` : "";
  const title = (row.title || "").trim();
  const ref = (row.ref || "").trim();
  if (title) return `${num ? num + " " : ""}${title}`;
  if (num) return num;
  if (ref) return ref.replace(/^refs\/(heads|tags)\//, "");
  return "";
}
const rowKey = (r) => `${r.event_type}|${r.action}|${r.actor_login}|${r.event_ts}|${r.number}`;
const tsMs = (r) => parseTs(r?.event_ts) || 0; // event-time epoch (ms, UTC); 0 if unparseable

const feed = $("feed");
const seen = new Set();
// The feed is a rolling window: the live stream inserts at the top and trims the
// oldest beyond `feedCap`. `feedCap` GROWS with each "Load more" so paged-in older
// rows survive (they'd otherwise be trimmed the instant they append at the bottom);
// it starts a little above the initial backfill (PAGE) to leave live-event headroom.
const PAGE = 50; // initial backfill size AND the SDK cursor page size (see startFeed)
let feedCap = PAGE + 10;
function feedEl(row) {
  const [verb, tone] = describe(row);
  const li = document.createElement("li");
  li.className = "event";
  const login = row.actor_login || "someone";
  // actor_url / actor_avatar_url are extracted at ingest (and backfilled on old rows);
  // fall back to the derivable forms if ever absent. target_url links the issue/PR/run.
  const profile = row.actor_url || `https://github.com/${encodeURIComponent(row.actor_login || "")}`;
  const avatar = row.actor_avatar_url || "";
  const detail = detailText(row);
  const target = row.target_url || "";
  // Avatar (linked to the profile, tone-colored ring) replaces the bare dot; keep the
  // dot as a fallback if a row somehow has no avatar. referrerpolicy keeps GH out of our refs.
  const lead = avatar
    ? `<a class="ev-avatar tone-ring-${tone}" href="${esc(profile)}" rel="noreferrer" title="${esc(login)}"><img src="${esc(avatar)}" alt="" width="24" height="24" loading="lazy" referrerpolicy="no-referrer"></a>`
    : `<span class="ev-dot tone-${tone}"></span>`;
  // One target link per event, on the most specific text: the detail (issue/PR/run
  // title) when there is one, else the verb itself — so detail-less events (star,
  // watch, fork) still link to their target (the repo) instead of only the profile.
  const detailHtml = !detail ? ""
    : target ? `<a class="ev-detail" href="${esc(target)}" rel="noreferrer">${esc(detail)}</a>`
    : `<span class="ev-detail">${esc(detail)}</span>`;
  const verbHtml = (!detail && target)
    ? `<a class="ev-verb ev-link" href="${esc(target)}" rel="noreferrer">${esc(verb)}</a>`
    : `<span class="ev-verb">${esc(verb)}</span>`;
  li.innerHTML = `
    ${lead}
    <span class="ev-body">
      <a class="ev-actor" href="${esc(profile)}" rel="noreferrer">${esc(login)}</a>
      ${verbHtml}
      ${detailHtml}
    </span>
    <time class="ev-time" datetime="${esc(row.event_ts || "")}">${esc(relTime(row.event_ts))}</time>`;
  return li;
}
// Insert keeping the feed ordered by event_ts DESC. Streamed events do NOT arrive in
// event_ts order — GitHub delivers bursts unordered and SSE relays them by arrival —
// so a blind prepend renders the feed jumbled. Insert before the first strictly-older
// row; equal timestamps keep arrival order, since event_ts is only second-granular.
function addRow(row) {
  if (!row || !row.event_type) return false;
  const k = rowKey(row);
  if (seen.has(k)) return false;
  seen.add(k);
  const el = feedEl(row);
  el.dataset.k = k;
  const t = tsMs(row);
  el.dataset.ts = String(t);
  let ref = null;
  for (const child of feed.children) {
    if (t > Number(child.dataset.ts)) { ref = child; break; }
  }
  if (ref) feed.insertBefore(el, ref); else feed.append(el);
  while (feed.children.length > feedCap) {
    const last = feed.lastChild;
    seen.delete(last.dataset.k);
    feed.removeChild(last);
  }
  $("feed-empty").hidden = true;
  return true;
}

// --- Load more (SDK keyset cursor) ------------------------------------------
// Seeded from the liveQuery backfill's own Result, then walked backwards. Keyset
// paging is stable against live inserts at the top; same-second rows straddling a
// page boundary can be skipped, which is fine for a feed and not for an audit log.
let moreCursor = null;   // () => Promise<Result> for the next older page, or null when exhausted
let loadingMore = false;
function updateMore() {
  const btn = $("feed-more");
  if (!btn) return;
  btn.hidden = !moreCursor;
  btn.disabled = loadingMore;
  btn.textContent = loadingMore ? "Loading…" : "Load more";
}
async function loadMore() {
  if (!moreCursor || loadingMore) return;
  loadingMore = true; updateMore();
  const cursor = moreCursor;
  try {
    const res = await cursor();
    if (res?.ok && res.data?.length) {
      feedCap += res.data.length;      // grow BEFORE inserting so the older rows aren't trimmed on arrival
      res.data.forEach(addRow);        // sorted-insert by event_ts → older rows land at the bottom
      moreCursor = res.hasMore && res.next ? res.next : null;
    } else if (res?.ok) {
      moreCursor = null;               // empty page → nothing older left
    }
    // on a non-ok result or a throw: keep `moreCursor` so the button re-arms for a retry
  } catch { /* transient — leave the cursor in place to retry */ }
  loadingMore = false; updateMore();
}

function setStream(state) {
  const el = $("stream-status");
  const live = state === "live";
  const bad = state === "error" || state === "closed";
  el.dataset.state = live ? "live" : "other";
  el.querySelector(".dot").className = "dot " + (live ? "up" : bad ? "down" : "pending");
  el.querySelector(".stream-label").textContent = live ? "live" : bad ? "disconnected" : `${state}…`;
}

// liveQuery = initial backfill (.fetch) + live stream, with arrival dedup. The
// SDK uses the browser's native EventSource (auto-reconnect + Last-Event-ID),
// which rides through the ~2-min proxy idle reset (WaveHouse #226, shipped).
function startFeed() {
  setStream("connecting");
  wh.from("gh_events")
    .select("event_type", "action", "actor_login", "repo_name", "event_ts", "number", "title", "ref",
            "actor_avatar_url", "actor_url", "target_url")
    .orderBy("event_ts", "desc")
    .limit(PAGE)
    .liveQuery({
      initial: (r) => {
        if (r.ok) {
          r.data.forEach((row) => addRow(row)); // sorted-insert keeps event_ts-desc order
          if (!r.data.length) $("feed-empty").hidden = false;
          // Seed load-more from THIS backfill's Result — same query, so its next()
          // pages strictly older than the 50 rows we just rendered (no overlap).
          moreCursor = r.hasMore && r.next ? r.next : null;
          updateMore();
        } else {
          $("feed-empty").hidden = false;
          $("feed-empty").textContent = "Activity unavailable.";
        }
      },
      next: (ev) => {
        const row = ev?.data ?? ev;
        if (ev?.timestamp) recordLatency(ev.timestamp); // #33: ingest → browser latency
        bumpEpm(); // #34: this minute's bucket just grew
        if (addRow(row) && (row.event_type === "star" || row.event_type === "fork")) refreshCounts();
      },
      status: (s) => setStream(s),
      error: () => setStream("error"),
    });
}

// --- Completeness (gh_health pipe) ------------------------------------------
// The drift alarm. The Worker's snapshot cron appends GitHub's authoritative totals to
// gh_snapshots; the pipe compares the newest row against what the log holds and
// returns the delta. This line replaces a GitHub Actions reconciler that filed issues.
//
// Drift is DETECTED, not healed — GitHub closed stargazer listing to every
// read-only credential, so there is no per-user set left to backfill from.
// Derived from the cron schedule at build time (see build.mjs) so the two stay in step.
const HEALTH_STALE_S = __STALE_AFTER_S__;

const signed = (n) => `${n > 0 ? "+" : ""}${fmtInt(n)}`;

async function loadHealth() {
  const el = $("health");
  if (!el) return;
  const { ok, data } = await wh.pipe("gh_health").fetch();
  if (!ok || !data?.length) { el.hidden = true; return; }

  const h = data[0];
  const n = (v) => Number(v ?? 0);
  const checked = n(h.checked_at);
  const now = n(h.now_at) || Math.floor(Date.now() / 1000);
  const drifts = [
    ["star", n(h.star_drift), n(h.gh_stars)],
    ["fork", n(h.fork_drift), n(h.gh_forks)],
  ].filter(([, d]) => d !== 0);

  let state, text;
  if (!checked) {
    // First deploy, or the cron has never fired.
    state = "pending";
    text = "completeness check has not run yet";
  } else if (now - checked > HEALTH_STALE_S) {
    // The cron itself stopped — which is also how a silently-broken write path
    // (an expired writer JWT, a rotated secret) shows up.
    state = "down";
    text = `completeness check stale — last ran ${relTime(new Date(checked * 1000).toISOString())}`;
  } else if (drifts.length) {
    state = "warn";
    text = `feed drift · ${drifts.map(([k, d, gh]) => `${k}s ${signed(d)} vs GitHub's ${fmtInt(gh)}`).join(" · ")}`;
  } else {
    state = "up";
    text = `feed complete · ${fmtInt(n(h.gh_stars))} stars and ${fmtInt(n(h.gh_forks))} forks match GitHub`;
  }

  $("health-dot").className = `dot ${state}`;
  $("health-text").innerHTML = state === "up"
    ? esc(text).replace(/(\d[\d,]*)/g, "<b>$1</b>")
    : esc(text);
  el.title = `gh_health · checked ${checked ? new Date(checked * 1000).toISOString() : "never"}`;
  el.hidden = false;
}

// --- Totals over time (gh_totals_history pipe) ------------------------------
// GitHub's own star/fork totals, sampled by the cron into the append-only snapshot log.
// The event feed can't produce this: it only starts at the webhook install, so any
// star that predates it is invisible to the feed but present in GitHub's count.
//
// Sparse by nature — a fresh deploy has one point. Stay hidden below two.
async function loadTotals() {
  const card = $("totals-card");
  if (!card) return;
  const { ok, data } = await wh.pipe("gh_totals_history").fetch();
  const pts = (ok && Array.isArray(data) ? data : [])
    .map((r) => ({ t: parseTs(r.ts), stars: Number(r.stars) || 0 }))
    .filter((r) => Number.isFinite(r.t));

  if (pts.length < 2) { card.hidden = true; return; }

  const vals = pts.map((p) => p.stars);
  const min = Math.min(...vals), max = Math.max(...vals);
  // A flat series would divide by zero; give it a floor so the line sits mid-height.
  const span = max - min || 1;
  const W = 600, H = 90, pad = 6, n = vals.length;
  const x = (i) => pad + (i * (W - 2 * pad)) / (n - 1);
  const y = (v) => H - pad - ((v - min) / span) * (H - 2 * pad);
  const line = vals.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  $("totals-line").setAttribute("d", line);
  $("totals-area").setAttribute("d", `${line} L${x(n - 1).toFixed(1)} ${H} L${x(0).toFixed(1)} ${H} Z`);
  $("totals-range").textContent = `${fmtInt(min)} → ${fmtInt(max)} stars`;
  $("totals-note").textContent = `${n} samples since ${relTime(new Date(pts[0].t).toISOString())}`;
  card.hidden = false;
}

// Name the repo everywhere the page mentions it. The markup ships with a placeholder
// so the file reads sensibly on its own; this is what makes it match the deployment.
function applyRepoName() {
  document.title = `${GH_REPO} stats — live GitHub activity, served by WaveHouse`;
  for (const el of document.querySelectorAll("[data-repo]")) el.textContent = GH_REPO;
  for (const el of document.querySelectorAll("[data-repo-href]")) el.href = `https://github.com/${GH_REPO}`;
}

// --- Boot -------------------------------------------------------------------
applyRepoName();
loadCount("stat-stars", "gh_stars_total");
loadCount("stat-forks", "gh_forks_total");
loadChart();
loadEpm(); // #34: events/min sparkline (then bumped live by the feed)
loadSummary(); // #34: lifetime "N events tracked" total
loadHealth(); // completeness vs GitHub's authoritative totals (gh_health)
loadTotals(); // GitHub's totals over time, from the append-only snapshot log
startFeed();
$("feed-more")?.addEventListener("click", loadMore); // paginate older events via the SDK cursor
initAnalytics(wh); // engineering-activity analytics sections (see analytics.js), same SDK client
setInterval(refreshCounts, 60_000); // safety net for any missed count-affecting event
setInterval(() => { loadEpm(); loadSummary(); }, 60_000); // reconcile sparkline/total with the server
setInterval(() => { loadHealth(); loadTotals(); }, 300_000); // matches the snapshot cadence
setInterval(
  () => document.querySelectorAll("time.ev-time").forEach((t) => { t.textContent = relTime(t.getAttribute("datetime")); }),
  30_000,
);
